import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import RefreshIcon from '@material-ui/icons/Refresh'
import GetAppIcon from '@material-ui/icons/GetApp'
import SearchIcon from '@material-ui/icons/Search'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ScheduleIcon from '@material-ui/icons/Schedule'
import DriveEtaIcon from '@material-ui/icons/DriveEta'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import WarningIcon from '@material-ui/icons/Warning'
import BlockIcon from '@material-ui/icons/Block'
import SaveIcon from '@material-ui/icons/Save'
import RestoreIcon from '@material-ui/icons/Restore'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import PersonIcon from '@material-ui/icons/Person'
import HistoryIcon from '@material-ui/icons/History'
import DoneIcon from '@material-ui/icons/Done'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import LockIcon from '@material-ui/icons/Lock'
import LockOpenIcon from '@material-ui/icons/LockOpen'
import { subscribe } from '../SocketIo/Socket'

const DAYS_PER_PAGE = 10

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    maxWidth: 1700,
    margin: '0 auto',
    padding: 8,
    backgroundColor: '#eee'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statsBar: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  statChip: {
    fontWeight: 600
  },
  searchAndPagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  searchField: {
    minWidth: 280
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dayCard: {
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark
    }
  },
  dayHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  dayHeaderChips: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  dayChip: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'inherit',
    fontWeight: 500,
    height: 24,
    fontSize: '0.75rem'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    minWidth: 800
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.text.secondary,
    padding: 4
  },
  returnRow: {
    backgroundColor: theme.palette.grey[50],
    '& td': {
      fontStyle: 'italic',
      color: theme.palette.text.secondary
    }
  },
  reorderWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: '#FFF3E0',
    borderRadius: 4,
    margin: theme.spacing(1, 2)
  },
  recalcSuccess: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
    margin: theme.spacing(1, 2)
  },
  completedRow: {
    backgroundColor: '#E8F5E9',
    '& td': {
      color: theme.palette.text.secondary
    }
  },
  bottomPagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 0)
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary
  }
}))

const IrrigationSchedulePage = (props) => {
  const classes = useStyles()
  const { url } = props

  const [fullSchedule, setFullSchedule] = useState(null)
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [reorderedDays, setReorderedDays] = useState({})
  const [collapsedDays, setCollapsedDays] = useState({})
  const [noIrrigationConfirm, setNoIrrigationConfirm] = useState(null) // { stop, dayIndex }

  // New state for save/load/recalculate
  const [saving, setSaving] = useState(false)
  const [hasOverride, setHasOverride] = useState(false)
  const [overrideVersion, setOverrideVersion] = useState(null)
  const [lastSavedBy, setLastSavedBy] = useState(null)
  const [lastSavedAt, setLastSavedAt] = useState(null)
  const [recalculating, setRecalculating] = useState({}) // { [dayIndex]: true }
  const [recalculated, setRecalculated] = useState({}) // { [dayIndex]: true } — shows green check
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [resetConfirm, setResetConfirm] = useState(false)
  const [backfillConfirm, setBackfillConfirm] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  const [markCompleteConfirm, setMarkCompleteConfirm] = useState(null) // { stop, dayIndex }

  // Jump to the page containing today's date (or closest future day)
  const jumpToToday = (schedule) => {
    if (!schedule || !schedule.length) { setCurrentPage(0); return }
    const today = moment().format('YYYY-MM-DD')
    let todayIdx = schedule.findIndex(d => d.date >= today)
    if (todayIdx === -1) todayIdx = schedule.length - 1 // all in past, show last page
    setCurrentPage(Math.floor(todayIdx / DAYS_PER_PAGE))
  }

  const fetchSchedule = useCallback(async () => {
    setLoading(true)
    try {
      // First check for a saved override
      const overrideResp = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=GET_SCHEDULE_OVERRIDE`,
        {}
      )
      if (overrideResp.data?.success && overrideResp.data?.data) {
        const override = overrideResp.data.data
        setFullSchedule(override.schedule || [])
        setSummary(override.summary || null)
        setHasOverride(true)
        setOverrideVersion(override.version)
        setLastSavedBy(override.lastModifiedByName)
        setLastSavedAt(override.lastModifiedAt)
        setReorderedDays({})
        setHasUnsavedChanges(false)
        setRecalculated({})
        jumpToToday(override.schedule || [])
        return
      }

      // No override — fetch auto-generated schedule
      const response = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=GET_IRRIGATION_INSPECTION_SCHEDULE`,
        { numDays: 999 }
      )
      if (response.data?.success && response.data?.data) {
        setFullSchedule(response.data.data.schedule || [])
        setSummary(response.data.data.summary || null)
        setHasOverride(false)
        setOverrideVersion(null)
        setLastSavedBy(null)
        setLastSavedAt(null)
        setReorderedDays({})
        setHasUnsavedChanges(false)
        setRecalculated({})
        jumpToToday(response.data.data.schedule || [])
      }
    } catch (err) {
      console.error('Failed to fetch schedule:', err)
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    fetchSchedule()
  }, [fetchSchedule])

  // Real-time socket updates — when an inspection is completed, mark the stop live
  useEffect(() => {
    const cleanup = subscribe('irrigation:scheduleUpdate', (data) => {
      if (data?.action === 'stop_completed' && data?.siteId) {
        const siteIdStr = String(data.siteId)
        setFullSchedule(prev => {
          if (!prev) return prev
          return prev.map(day => {
            const hasMatch = day.stops.some(s => String(s.siteId) === siteIdStr)
            if (!hasMatch) return day
            return {
              ...day,
              stops: day.stops.map(s => {
                if (String(s.siteId) !== siteIdStr) return s
                return {
                  ...s,
                  status: 'completed',
                  completedAt: data.completedAt,
                  completedByName: data.completedByName
                }
              })
            }
          })
        })
        // Also update reorderedDays if present
        setReorderedDays(prev => {
          const next = { ...prev }
          let changed = false
          Object.keys(next).forEach(key => {
            const updated = next[key].map(s => {
              if (String(s.siteId) !== siteIdStr) return s
              changed = true
              return { ...s, status: 'completed', completedAt: data.completedAt, completedByName: data.completedByName }
            })
            next[key] = updated
          })
          return changed ? next : prev
        })
      }
    })
    return () => cleanup()
  }, [])

  const handleExport = async () => {
    setExporting(true)
    try {
      const response = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=EXPORT_IRRIGATION_SCHEDULE`,
        { numDays: 999 },
        { responseType: 'blob' }
      )
      if (response && response.data) {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', `inspection_schedule_${moment().format('YYYY-MM-DD')}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      }
    } catch (err) {
      console.error('Failed to export schedule:', err)
    } finally {
      setExporting(false)
    }
  }

  // Recalculate drive times for a day via ORS
  // stopsOverride: pass stops directly when called from handleDragEnd to avoid stale state
  const recalculateDay = async (dayIdx, stopsOverride) => {
    const stops = stopsOverride || getStopsForDay(dayIdx)
    if (!stops || stops.length === 0) return

    setRecalculating(prev => ({ ...prev, [dayIdx]: true }))
    setRecalculated(prev => ({ ...prev, [dayIdx]: false }))

    try {
      const response = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=RECALCULATE_SCHEDULE_DAY`,
        { stops }
      )
      if (response.data?.success && response.data?.data) {
        const { stops: newStops, totalDriveMinutes, totalDriveMiles, totalWorkMinutes, returnToYard } = response.data.data

        // Update reorderedDays with the recalculated stops
        setReorderedDays(prev => ({ ...prev, [dayIdx]: newStops }))

        // Update fullSchedule day-level totals
        setFullSchedule(prev => {
          if (!prev) return prev
          return prev.map((day, idx) => {
            if (idx !== dayIdx) return day
            return {
              ...day,
              stops: newStops,
              totalDriveMinutes,
              totalDriveMiles,
              totalWorkMinutes,
              returnToYard
            }
          })
        })

        setRecalculated(prev => ({ ...prev, [dayIdx]: true }))
      }
    } catch (err) {
      console.error('Failed to recalculate day:', err)
    } finally {
      setRecalculating(prev => ({ ...prev, [dayIdx]: false }))
    }
  }

  // Save schedule override
  const handleSave = async () => {
    if (!fullSchedule) return
    setSaving(true)
    try {
      // Merge reorderedDays into the schedule
      const mergedSchedule = fullSchedule.map((day, idx) => {
        const stops = reorderedDays[idx] || day.stops
        return { ...day, stops, totalStops: stops.length }
      }).filter(day => day.stops.length > 0)

      const response = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=SAVE_SCHEDULE_OVERRIDE`,
        {
          schedule: mergedSchedule,
          summary,
          changeAction: 'save_schedule',
          changeMetadata: { totalDays: mergedSchedule.length }
        }
      )
      if (response.data?.success) {
        setFullSchedule(mergedSchedule)
        setReorderedDays({})
        setHasUnsavedChanges(false)
        setHasOverride(true)
        setOverrideVersion(response.data.data.version)
        setLastSavedBy(response.data.data.lastModifiedByName)
        setLastSavedAt(response.data.data.lastModifiedAt)
      }
    } catch (err) {
      console.error('Failed to save schedule:', err)
    } finally {
      setSaving(false)
    }
  }

  // Reset to auto-generated schedule
  const handleReset = async () => {
    try {
      await axios.post(
        `${url}/api/hmi/genericPostRequest?type=RESET_SCHEDULE_OVERRIDE`,
        {}
      )
      setResetConfirm(false)
      fetchSchedule()
    } catch (err) {
      console.error('Failed to reset schedule:', err)
    }
  }

  // Backfill historical inspections into the schedule
  const handleBackfill = async () => {
    setBackfilling(true)
    try {
      const resp = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=BACKFILL_SCHEDULE_FROM_INSPECTIONS`,
        {}
      )
      setBackfillConfirm(false)
      if (resp?.data?.data) {
        const { backfilledCount, totalCompleted, totalStops, totalDays } = resp.data.data
        alert(`Backfill complete!\n\n${backfilledCount} inspections matched.\n${totalCompleted}/${totalStops} stops marked completed across ${totalDays} days.`)
      }
      fetchSchedule()
    } catch (err) {
      console.error('Failed to backfill schedule:', err)
      alert('Backfill failed - check console for details')
    } finally {
      setBackfilling(false)
    }
  }

  // Mark site as no irrigation system
  const handleMarkNoIrrigation = async () => {
    if (!noIrrigationConfirm) return
    const { stop, dayIndex } = noIrrigationConfirm
    try {
      const response = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=TOGGLE_NO_IRRIGATION`,
        { jobSiteId: stop.siteId, noIrrigationSystem: true }
      )
      if (response?.data?.success) {
        // Remove site from schedule in local state
        removeSiteFromSchedule(stop.siteId)
        setHasUnsavedChanges(true)
        // Recalculate affected day
        const dayIdx = dayIndex
        // Small delay to let state settle, then recalculate
        setTimeout(() => recalculateDay(dayIdx), 100)
      }
    } catch (e) {
      console.error('Error marking no irrigation:', e)
    } finally {
      setNoIrrigationConfirm(null)
    }
  }

  // Manually mark a stop as completed
  const handleMarkComplete = async () => {
    if (!markCompleteConfirm) return
    const { stop, dayIndex } = markCompleteConfirm
    try {
      const resp = await axios.post(
        `${url}/api/hmi/genericPostRequest?type=MARK_STOP_COMPLETED`,
        { siteId: stop.siteId }
      )
      if (resp?.data?.success) {
        // Update local state — mark stop as completed
        const siteIdStr = String(stop.siteId)
        setFullSchedule(prev => {
          if (!prev) return prev
          return prev.map((day, dayIdx) => {
            const currentStops = reorderedDays[dayIdx] || day.stops
            const updated = currentStops.map(s => {
              if (String(s.siteId) === siteIdStr) {
                return { ...s, status: 'completed', completedAt: new Date(), completedByName: 'Manual' }
              }
              return s
            })
            return { ...day, stops: updated }
          })
        })
        setReorderedDays(prev => {
          const next = { ...prev }
          Object.keys(next).forEach(key => {
            next[key] = next[key].map(s => {
              if (String(s.siteId) === siteIdStr) {
                return { ...s, status: 'completed', completedAt: new Date(), completedByName: 'Manual' }
              }
              return s
            })
          })
          return next
        })
        setHasUnsavedChanges(true)
      }
    } catch (e) {
      console.error('Error marking stop complete:', e)
    } finally {
      setMarkCompleteConfirm(null)
    }
  }

  // Remove a site from the local schedule state (after marking no irrigation)
  const removeSiteFromSchedule = (siteId) => {
    const siteIdStr = String(siteId)
    setFullSchedule(prev => {
      if (!prev) return prev
      return prev.map((day, dayIdx) => {
        const currentStops = reorderedDays[dayIdx] || day.stops
        const filtered = currentStops.filter(s => String(s.siteId) !== siteIdStr)
        if (filtered.length === currentStops.length) return day
        // Renumber
        filtered.forEach((s, i) => { s.order = i + 1 })
        return {
          ...day,
          stops: filtered,
          totalStops: filtered.length,
          totalDriveMinutes: day.totalDriveMinutes, // keep original estimate
          totalDriveMiles: day.totalDriveMiles
        }
      }).filter(day => day.stops.length > 0)
    })
    // Clear any reorder state for affected days
    setReorderedDays(prev => {
      const next = { ...prev }
      Object.keys(next).forEach(key => {
        next[key] = next[key].filter(s => String(s.siteId) !== siteIdStr)
        if (next[key].length === 0) delete next[key]
        else next[key].forEach((s, i) => { s.order = i + 1 })
      })
      return next
    })
  }

  // Lock/unlock a stop in the schedule
  const handleToggleLock = async (dayIndex, stopIndex) => {
    const stops = getStopsForDay(dayIndex)
    const stop = stops[stopIndex]
    if (!stop) return
    const isLocked = stop.locked
    const type = isLocked ? 'UNLOCK_SCHEDULE_STOP' : 'LOCK_SCHEDULE_STOP'
    try {
      await axios.post(`${url}/api/hmi/genericPostRequest?type=${type}`, { siteId: stop.siteId })
      // Update local state
      const updatedStops = Array.from(stops)
      updatedStops[stopIndex] = {
        ...updatedStops[stopIndex],
        locked: !isLocked,
        lockedByName: !isLocked ? 'You' : null,
        lockedAt: !isLocked ? new Date().toISOString() : null
      }
      setReorderedDays(prev => ({ ...prev, [dayIndex]: updatedStops }))
    } catch (err) {
      console.error('[handleToggleLock] Error:', err)
    }
  }

  // Move a stop to the very top of the schedule (first pending position in first day)
  const handlePrioritize = (dayIndex, stopIndex) => {
    if (!fullSchedule || !fullSchedule.length) return
    const stopToMove = getStopsForDay(dayIndex)[stopIndex]
    if (stopToMove?.locked) return
    // Find the first day that has pending stops (today)
    const firstDayIdx = fullSchedule.findIndex(d => {
      const stops = getStopsForDay(fullSchedule.indexOf(d))
      return stops.some(s => s.status !== 'completed')
    })
    const targetDayIdx = firstDayIdx !== -1 ? firstDayIdx : 0

    if (targetDayIdx === dayIndex) {
      // Same day — just move to top after completed stops
      const stops = Array.from(getStopsForDay(dayIndex))
      const [moved] = stops.splice(stopIndex, 1)
      const lastCompletedIdx = stops.reduce((acc, s, i) => s.status === 'completed' ? i : acc, -1)
      stops.splice(lastCompletedIdx + 1, 0, moved)
      stops.forEach((s, i) => { s.order = i + 1 })
      setReorderedDays(prev => ({ ...prev, [dayIndex]: stops }))
      setHasUnsavedChanges(true)
      recalculateDay(dayIndex, stops)
    } else {
      // Cross-day move — remove from source, insert at top of target
      const sourceStops = Array.from(getStopsForDay(dayIndex))
      const destStops = Array.from(getStopsForDay(targetDayIdx))
      const [moved] = sourceStops.splice(stopIndex, 1)
      const lastCompletedIdx = destStops.reduce((acc, s, i) => s.status === 'completed' ? i : acc, -1)
      destStops.splice(lastCompletedIdx + 1, 0, moved)
      sourceStops.forEach((s, i) => { s.order = i + 1 })
      destStops.forEach((s, i) => { s.order = i + 1 })
      setReorderedDays(prev => ({ ...prev, [dayIndex]: sourceStops, [targetDayIdx]: destStops }))
      setFullSchedule(prev => {
        if (!prev) return prev
        return prev.map((day, idx) => {
          if (idx === dayIndex) return { ...day, totalStops: sourceStops.length }
          if (idx === targetDayIdx) return { ...day, totalStops: destStops.length }
          return day
        }).filter(day => day.totalStops > 0)
      })
      setHasUnsavedChanges(true)
      recalculateDay(targetDayIdx, destStops)
      if (sourceStops.length > 0) recalculateDay(dayIndex, sourceStops)
    }
  }

  // Move a stop to the very end of the schedule (last day, last position)
  const handleDeprioritize = (dayIndex, stopIndex) => {
    if (!fullSchedule || !fullSchedule.length) return
    const stopToMove = getStopsForDay(dayIndex)[stopIndex]
    if (stopToMove?.locked) return
    const lastDayIdx = fullSchedule.length - 1

    if (lastDayIdx === dayIndex) {
      // Same day — just move to bottom
      const stops = Array.from(getStopsForDay(dayIndex))
      const [moved] = stops.splice(stopIndex, 1)
      stops.push(moved)
      stops.forEach((s, i) => { s.order = i + 1 })
      setReorderedDays(prev => ({ ...prev, [dayIndex]: stops }))
      setHasUnsavedChanges(true)
      recalculateDay(dayIndex, stops)
    } else {
      // Cross-day move — remove from source, append to last day
      const sourceStops = Array.from(getStopsForDay(dayIndex))
      const destStops = Array.from(getStopsForDay(lastDayIdx))
      const [moved] = sourceStops.splice(stopIndex, 1)
      destStops.push(moved)
      sourceStops.forEach((s, i) => { s.order = i + 1 })
      destStops.forEach((s, i) => { s.order = i + 1 })
      setReorderedDays(prev => ({ ...prev, [dayIndex]: sourceStops, [lastDayIdx]: destStops }))
      setFullSchedule(prev => {
        if (!prev) return prev
        return prev.map((day, idx) => {
          if (idx === dayIndex) return { ...day, totalStops: sourceStops.length }
          if (idx === lastDayIdx) return { ...day, totalStops: destStops.length }
          return day
        }).filter(day => day.totalStops > 0)
      })
      setHasUnsavedChanges(true)
      recalculateDay(lastDayIdx, destStops)
      if (sourceStops.length > 0) recalculateDay(dayIndex, sourceStops)
    }
  }

  // Get the stops for a day (possibly reordered)
  const getStopsForDay = (dayIndex) => {
    if (reorderedDays[dayIndex]) return reorderedDays[dayIndex]
    return fullSchedule?.[dayIndex]?.stops || []
  }

  // Filter days by search term
  const getFilteredDays = () => {
    if (!fullSchedule) return []
    if (!searchTerm.trim()) return fullSchedule

    const term = searchTerm.toLowerCase().trim()
    return fullSchedule.map((day, dayIndex) => {
      const stops = getStopsForDay(dayIndex)
      const filteredStops = stops.filter(s =>
        (s.siteName && s.siteName.toLowerCase().includes(term)) ||
        (s.address && s.address.toLowerCase().includes(term)) ||
        (s.communityName && s.communityName.toLowerCase().includes(term)) ||
        (s.buildingNumber && String(s.buildingNumber).toLowerCase().includes(term))
      )
      if (filteredStops.length === 0) return null
      return { ...day, stops: filteredStops, totalStops: filteredStops.length }
    }).filter(Boolean)
  }

  const filteredDays = getFilteredDays()
  const totalPages = Math.ceil(filteredDays.length / DAYS_PER_PAGE)
  const pagedDays = filteredDays.slice(currentPage * DAYS_PER_PAGE, (currentPage + 1) * DAYS_PER_PAGE)

  // Unified drag handler — supports within-day reorder and cross-day moves
  const handleDragEnd = (result) => {
    if (!result.destination) return
    const { source, destination } = result

    // droppableIds are like "day-3" → extract the day number
    const sourceDayNum = parseInt(source.droppableId.replace('day-', ''), 10)
    const destDayNum = parseInt(destination.droppableId.replace('day-', ''), 10)

    // Guard: reject drag of locked stops
    const sourceDayIdxCheck = fullSchedule.findIndex(d => d.day === sourceDayNum)
    if (sourceDayIdxCheck !== -1) {
      const draggedStop = getStopsForDay(sourceDayIdxCheck)[source.index]
      if (draggedStop?.locked) return
    }

    // Map day number back to fullSchedule index
    const sourceDayIdx = fullSchedule.findIndex(d => d.day === sourceDayNum)
    const destDayIdx = fullSchedule.findIndex(d => d.day === destDayNum)
    if (sourceDayIdx === -1 || destDayIdx === -1) return

    // Map of dayIdx → stops to recalculate (pass directly to avoid stale state)
    const daysToRecalc = {}

    if (sourceDayIdx === destDayIdx) {
      // Same-day reorder
      if (source.index === destination.index) return
      const stops = Array.from(getStopsForDay(sourceDayIdx))
      const [moved] = stops.splice(source.index, 1)
      stops.splice(destination.index, 0, moved)
      stops.forEach((s, i) => { s.order = i + 1 })
      setReorderedDays(prev => ({ ...prev, [sourceDayIdx]: stops }))
      daysToRecalc[sourceDayIdx] = stops
    } else {
      // Cross-day move
      const sourceStops = Array.from(getStopsForDay(sourceDayIdx))
      const destStops = Array.from(getStopsForDay(destDayIdx))
      const [moved] = sourceStops.splice(source.index, 1)
      destStops.splice(destination.index, 0, moved)

      sourceStops.forEach((s, i) => { s.order = i + 1 })
      destStops.forEach((s, i) => { s.order = i + 1 })

      setReorderedDays(prev => ({
        ...prev,
        [sourceDayIdx]: sourceStops,
        [destDayIdx]: destStops
      }))

      // Update the day-level stop counts in fullSchedule
      setFullSchedule(prev => {
        if (!prev) return prev
        return prev.map((day, idx) => {
          if (idx === sourceDayIdx) {
            return { ...day, totalStops: sourceStops.length }
          }
          if (idx === destDayIdx) {
            return { ...day, totalStops: destStops.length }
          }
          return day
        }).filter(day => day.totalStops > 0)
      })

      if (sourceStops.length > 0) daysToRecalc[sourceDayIdx] = sourceStops
      daysToRecalc[destDayIdx] = destStops
    }

    setHasUnsavedChanges(true)
    // Clear previous recalc status for affected days
    const affectedDayIdxs = Object.keys(daysToRecalc).map(Number)
    setRecalculated(prev => {
      const next = { ...prev }
      affectedDayIdxs.forEach(d => { next[d] = false })
      return next
    })

    // Recalculate affected days — pass stops directly to avoid stale closure
    affectedDayIdxs.forEach(dayIdx => {
      recalculateDay(dayIdx, daysToRecalc[dayIdx])
    })
  }

  const toggleDayCollapse = (dayIndex) => {
    setCollapsedDays(prev => ({ ...prev, [dayIndex]: !prev[dayIndex] }))
  }

  const isDayReordered = (dayIndex) => !!reorderedDays[dayIndex]

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingWrapper}>
          <CircularProgress size={48} />
        </div>
      </div>
    )
  }

  const dragDisabled = !!searchTerm

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <IconButton component={Link} to="/hmiirrigationdashboard" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Irrigation Inspection Schedule
          </Typography>
        </div>
        <div className={classes.headerActions}>
          {hasUnsavedChanges && (
            <Button
              variant="contained"
              size="small"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: '#4CAF50', color: '#fff' }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
          {hasOverride && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestoreIcon />}
              onClick={() => setResetConfirm(true)}
              style={{ borderColor: '#FF9800', color: '#FF9800' }}
            >
              Reset to Auto
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={backfilling ? <CircularProgress size={16} /> : <HistoryIcon />}
            onClick={() => setBackfillConfirm(true)}
            disabled={backfilling}
            style={{ borderColor: '#2196F3', color: '#2196F3' }}
          >
            {backfilling ? 'Backfilling...' : 'Backfill History'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchSchedule}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
            onClick={handleExport}
            disabled={exporting || !fullSchedule?.length}
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {summary && (
        <div className={classes.statsBar}>
          <Chip
            icon={<LocationOnIcon />}
            label={`${summary.totalSitesRemaining + summary.totalSitesScheduled} sites remaining`}
            className={classes.statChip}
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<ScheduleIcon />}
            label={`${summary.totalAlreadyInspected} already inspected`}
            className={classes.statChip}
            style={{ borderColor: '#4CAF50', color: '#4CAF50' }}
            variant="outlined"
          />
          <Chip
            icon={<DriveEtaIcon />}
            label={`~${summary.estimatedTotalDaysToComplete} days to complete`}
            className={classes.statChip}
            style={{ borderColor: '#FF9800', color: '#FF9800' }}
            variant="outlined"
          />
          <Chip
            label={`${summary.totalMCFMSites} total MCFM sites`}
            className={classes.statChip}
            variant="outlined"
          />
          {lastSavedBy && lastSavedAt && (
            <Chip
              icon={<PersonIcon />}
              label={`Last saved by ${lastSavedBy} ${moment(lastSavedAt).fromNow()}`}
              className={classes.statChip}
              style={{ borderColor: '#1976d2', color: '#1976d2' }}
              variant="outlined"
            />
          )}
        </div>
      )}

      {/* Search + Pagination */}
      <div className={classes.searchAndPagination}>
        <TextField
          className={classes.searchField}
          size="small"
          variant="outlined"
          placeholder="Search by site name, address, community..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            )
          }}
        />
        {totalPages > 1 && (
          <div className={classes.paginationControls}>
            <IconButton
              size="small"
              onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <Typography variant="body2">
              Page {currentPage + 1} of {totalPages}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              <NavigateNextIcon />
            </IconButton>
          </div>
        )}
      </div>

      {/* No results */}
      {!fullSchedule?.length && !loading && (
        <div className={classes.emptyState}>
          <Typography variant="h6">No inspection schedule available</Typography>
          <Typography variant="body2">All MCFM sites may have been inspected, or no sites have valid coordinates.</Typography>
        </div>
      )}

      {filteredDays.length === 0 && fullSchedule?.length > 0 && searchTerm && (
        <div className={classes.emptyState}>
          <Typography variant="body1">No sites match "{searchTerm}"</Typography>
        </div>
      )}

      {/* Single DragDropContext wrapping all day cards for cross-day support */}
      <DragDropContext onDragEnd={handleDragEnd}>
        {pagedDays.map((day) => {
          const origDayIndex = fullSchedule.findIndex(d => d.day === day.day)
          const stops = searchTerm ? day.stops : getStopsForDay(origDayIndex)
          const isCollapsed = collapsedDays[origDayIndex]
          const isReordered = isDayReordered(origDayIndex)
          const isRecalculating = recalculating[origDayIndex]
          const isRecalculated = recalculated[origDayIndex]
          const completedCount = stops.filter(s => s.status === 'completed').length
          const pendingCount = stops.length - completedCount

          return (
            <Paper key={day.day} className={classes.dayCard} elevation={1}>
              <div
                className={classes.dayHeader}
                onClick={() => toggleDayCollapse(origDayIndex)}
              >
                <div className={classes.dayHeaderLeft}>
                  {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    Day {day.day} — {day.dateLabel}
                  </Typography>
                </div>
                <div className={classes.dayHeaderChips}>
                  {isRecalculating && (
                    <Chip
                      icon={<CircularProgress size={12} style={{ color: 'inherit' }} />}
                      label="Recalculating..."
                      size="small"
                      className={classes.dayChip}
                    />
                  )}
                  <Chip
                    label={completedCount > 0 ? `${completedCount}/${stops.length} done` : `${stops.length} stops`}
                    size="small"
                    className={classes.dayChip}
                    style={completedCount === stops.length ? { backgroundColor: 'rgba(76,175,80,0.4)' } : undefined}
                  />
                  <Chip
                    label={`${day.totalDriveMinutes} min drive`}
                    size="small"
                    className={classes.dayChip}
                  />
                  <Chip
                    label={`${day.totalDriveMiles} mi`}
                    size="small"
                    className={classes.dayChip}
                  />
                  {day.totalWorkMinutes != null && (
                    <Chip
                      label={`${Math.floor(day.totalWorkMinutes / 60)}h ${day.totalWorkMinutes % 60}m work`}
                      size="small"
                      className={classes.dayChip}
                      style={{ backgroundColor: 'rgba(76,175,80,0.3)' }}
                    />
                  )}
                </div>
              </div>

              <Collapse in={!isCollapsed}>
                {isRecalculating && (
                  <div className={classes.reorderWarning}>
                    <CircularProgress size={14} style={{ color: '#F57C00' }} />
                    <Typography variant="caption" style={{ color: '#E65100' }}>
                      Recalculating drive times...
                    </Typography>
                  </div>
                )}
                {!isRecalculating && isRecalculated && (
                  <div className={classes.recalcSuccess}>
                    <CheckCircleIcon style={{ fontSize: 16, color: '#4CAF50' }} />
                    <Typography variant="caption" style={{ color: '#2E7D32' }}>
                      Drive times recalculated
                    </Typography>
                  </div>
                )}
                {!isRecalculating && !isRecalculated && isReordered && (
                  <div className={classes.reorderWarning}>
                    <WarningIcon style={{ fontSize: 16, color: '#F57C00' }} />
                    <Typography variant="caption" style={{ color: '#E65100' }}>
                      Stops reordered — times are approximate
                    </Typography>
                  </div>
                )}
                <div className={classes.tableContainer}>
                  <Table className={classes.table} size="small">
                    <TableHead>
                      <TableRow>
                        {!dragDisabled && <TableCell style={{ width: 40 }}></TableCell>}
                        <TableCell style={{ width: 50 }}>#</TableCell>
                        <TableCell>Site Name</TableCell>
                        <TableCell>Building</TableCell>
                        <TableCell>Community</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell align="center">Arrival</TableCell>
                        <TableCell align="center">Departure</TableCell>
                        <TableCell align="right">Drive Min</TableCell>
                        <TableCell align="right">Drive Mi</TableCell>
                        <TableCell style={{ width: 130 }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <Droppable droppableId={`day-${day.day}`} isDropDisabled={dragDisabled}>
                      {(provided, droppableSnapshot) => (
                        <TableBody
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            backgroundColor: droppableSnapshot.isDraggingOver ? '#E8F5E9' : undefined,
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          {stops.map((stop, idx) => {
                            const isCompleted = stop.status === 'completed'
                            return (
                            <Draggable
                              key={`${day.day}-${stop.siteId || idx}`}
                              draggableId={`${day.day}-${stop.siteId || idx}`}
                              index={idx}
                              isDragDisabled={dragDisabled || isCompleted || stop.locked}
                            >
                              {(provided, snapshot) => (
                                <TableRow
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={isCompleted ? classes.completedRow : undefined}
                                  style={{
                                    ...provided.draggableProps.style,
                                    backgroundColor: snapshot.isDragging ? '#E3F2FD' : undefined
                                  }}
                                >
                                  {!dragDisabled && (
                                    <TableCell {...provided.dragHandleProps} className={classes.dragHandle}>
                                      {isCompleted
                                        ? <CheckCircleIcon fontSize="small" style={{ color: '#4CAF50' }} />
                                        : stop.locked
                                          ? <LockIcon fontSize="small" style={{ color: '#FF9800' }} />
                                          : <DragIndicatorIcon fontSize="small" />
                                      }
                                    </TableCell>
                                  )}
                                  <TableCell>{stop.order}</TableCell>
                                  <TableCell style={{ fontWeight: 500, textDecoration: isCompleted ? 'line-through' : 'none' }}>
                                    {stop.siteName}
                                    {stop.locked && !isCompleted && (
                                      <Chip
                                        icon={<LockIcon style={{ fontSize: 14, color: '#FF9800' }} />}
                                        label="Scheduled"
                                        size="small"
                                        style={{ marginLeft: 8, height: 22, backgroundColor: '#FFF3E0', color: '#E65100', fontWeight: 600, fontSize: 11 }}
                                      />
                                    )}
                                    {isCompleted && (
                                      <Typography variant="caption" style={{ display: 'block', color: '#4CAF50', textDecoration: 'none' }}>
                                        Completed{stop.completedByName ? ` by ${stop.completedByName}` : ''}{stop.completedAt ? ` ${moment(stop.completedAt).fromNow()}` : ''}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>{stop.buildingNumber || '-'}</TableCell>
                                  <TableCell>{stop.communityName || '-'}</TableCell>
                                  <TableCell>{stop.address || '-'}</TableCell>
                                  <TableCell align="center">{stop.arrivalTime}</TableCell>
                                  <TableCell align="center">{stop.departureTime}</TableCell>
                                  <TableCell align="right">{stop.driveTimeMinutes}</TableCell>
                                  <TableCell align="right">{stop.driveDistanceMiles}</TableCell>
                                  <TableCell align="center" style={{ padding: 2, whiteSpace: 'nowrap' }}>
                                    {isCompleted ? (
                                      <CheckCircleIcon fontSize="small" style={{ color: '#4CAF50' }} />
                                    ) : (
                                      <>
                                        <Tooltip title={stop.locked ? 'Unlock stop' : 'Lock to this date'}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => { e.stopPropagation(); handleToggleLock(origDayIndex, idx) }}
                                          >
                                            {stop.locked
                                              ? <LockIcon fontSize="small" style={{ color: '#FF9800' }} />
                                              : <LockOpenIcon fontSize="small" style={{ color: '#9e9e9e' }} />
                                            }
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Prioritize (move to top)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={stop.locked}
                                              onClick={(e) => { e.stopPropagation(); handlePrioritize(origDayIndex, idx) }}
                                            >
                                              <ArrowUpwardIcon fontSize="small" style={{ color: stop.locked ? '#ccc' : '#2196F3' }} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Deprioritize (move to bottom)">
                                          <span>
                                            <IconButton
                                              size="small"
                                              disabled={stop.locked}
                                              onClick={(e) => { e.stopPropagation(); handleDeprioritize(origDayIndex, idx) }}
                                            >
                                              <ArrowDownwardIcon fontSize="small" style={{ color: stop.locked ? '#ccc' : '#FF9800' }} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Mark as Completed">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setMarkCompleteConfirm({ stop, dayIndex: origDayIndex })
                                            }}
                                          >
                                            <DoneIcon fontSize="small" style={{ color: '#4CAF50' }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Mark as No Irrigation System">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setNoIrrigationConfirm({ stop, dayIndex: origDayIndex })
                                            }}
                                          >
                                            <BlockIcon fontSize="small" style={{ color: '#d32f2f' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                            )
                          })}
                          {provided.placeholder}
                          {/* Return to yard row */}
                          {!searchTerm && day.returnToYard && (
                            <TableRow className={classes.returnRow}>
                              {!dragDisabled && <TableCell></TableCell>}
                              <TableCell></TableCell>
                              <TableCell style={{ fontWeight: 500 }}>Return to Yard</TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                              <TableCell align="center">{day.returnToYard.estimatedArrival}</TableCell>
                              <TableCell></TableCell>
                              <TableCell align="right">{day.returnToYard.driveTimeMinutes}</TableCell>
                              <TableCell align="right">{day.returnToYard.driveDistanceMiles}</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      )}
                    </Droppable>
                  </Table>
                </div>
              </Collapse>
            </Paper>
          )
        })}
      </DragDropContext>

      {/* Bottom Pagination */}
      {totalPages > 1 && (
        <div className={classes.bottomPagination}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<NavigateBeforeIcon />}
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
          >
            Prev {DAYS_PER_PAGE}
          </Button>
          <Typography variant="body2" color="textSecondary">
            Showing days {currentPage * DAYS_PER_PAGE + 1} - {Math.min((currentPage + 1) * DAYS_PER_PAGE, filteredDays.length)} of {filteredDays.length}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            endIcon={<NavigateNextIcon />}
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
          >
            Next {DAYS_PER_PAGE}
          </Button>
        </div>
      )}

      {/* Confirm No Irrigation Dialog */}
      <Dialog
        open={!!noIrrigationConfirm}
        onClose={() => setNoIrrigationConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark as No Irrigation System?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>{noIrrigationConfirm?.stop?.siteName}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {noIrrigationConfirm?.stop?.address}
          </Typography>
          <Box mt={2}>
            <Typography variant="body2">
              This will flag the site as having no irrigation system and remove it from the inspection schedule.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoIrrigationConfirm(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkNoIrrigation}
            variant="contained"
            style={{ backgroundColor: '#d32f2f', color: '#fff' }}
          >
            Mark No Irrigation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetConfirm}
        onClose={() => setResetConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset to Auto-Generated Schedule?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will discard your saved customizations and revert to the automatically generated schedule. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleReset}
            variant="contained"
            style={{ backgroundColor: '#FF9800', color: '#fff' }}
          >
            Reset to Auto
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backfill Confirmation Dialog */}
      <Dialog
        open={backfillConfirm}
        onClose={() => setBackfillConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Backfill Historical Inspections?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will scan all completed irrigation inspections and mark matching sites as completed in the schedule. If no saved schedule exists, one will be created from all MCFM sites.
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic', color: '#666' }}>
            This is typically a one-time operation. Future inspections will automatically update the schedule in real-time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillConfirm(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBackfill}
            variant="contained"
            startIcon={backfilling ? <CircularProgress size={16} color="inherit" /> : <HistoryIcon />}
            disabled={backfilling}
            style={{ backgroundColor: '#2196F3', color: '#fff' }}
          >
            {backfilling ? 'Backfilling...' : 'Run Backfill'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Complete Confirmation Dialog */}
      <Dialog
        open={!!markCompleteConfirm}
        onClose={() => setMarkCompleteConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark as Completed?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Mark <strong>{markCompleteConfirm?.stop?.siteName}</strong> as completed?
          </Typography>
          <Typography variant="body2" style={{ marginTop: 4, color: '#666' }}>
            {markCompleteConfirm?.stop?.address}
          </Typography>
          <Typography variant="caption" style={{ marginTop: 8, display: 'block', color: '#999' }}>
            This will mark the stop as done on the schedule. Use this when the inspection was completed but didn't sync automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkCompleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkComplete}
            variant="contained"
            style={{ backgroundColor: '#4CAF50', color: '#fff' }}
          >
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(IrrigationSchedulePage)
